import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { GET_DEPARTMENTS, GET_SHIFTS } from '../../graphql/queries';

interface Position {
  id: string;
  name: string;
  description?: string;
  department?: string;
  departmentId?: string;
  defaultShiftId?: string | null;
  defaultShiftName?: string | null;
  isActive: boolean;
  employeeCount: number;
}

interface PositionDialogProps {
  position: Position | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string | null, data: any) => Promise<void>;
}

export const PositionDialog: React.FC<PositionDialogProps> = ({
  position,
  open,
  onClose,
  onSave,
}) => {
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    departmentId: '',
    defaultShiftId: '',
    isActive: true
  });

  const { data: departmentsData } = useQuery(GET_DEPARTMENTS);
  const departments = departmentsData?.departments || [];

  // Shift list needs `shifts.view`; without it the select just shows the current value.
  const { data: shiftsData, error: shiftsError } = useQuery(GET_SHIFTS, { skip: !open });
  const shifts: Array<{ id: string; name: string; isActive: boolean }> = shiftsData?.shifts || [];

  useEffect(() => {
    if (position) {
      // Edit mode
      setFormData({
        name: position.name,
        description: position.description || '',
        departmentId: position.departmentId || '',
        defaultShiftId: position.defaultShiftId || '',
        isActive: position.isActive
      });
    } else {
      // Create mode - reset form
      setFormData({
        name: '',
        description: '',
        departmentId: '',
        defaultShiftId: '',
        isActive: true
      });
    }
    setFormError('');
  }, [position, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Nazwa stanowiska jest wymagana');
      return;
    }

    try {
      setSaving(true);

      const saveData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        departmentId: formData.departmentId || undefined,
      };

      // Only include isActive when updating (not when creating)
      if (position) {
        saveData.isActive = formData.isActive;
        // Empty string clears the position's default shift on the backend.
        if (formData.defaultShiftId !== (position.defaultShiftId || '')) {
          saveData.defaultShiftId = formData.defaultShiftId;
        }
      } else if (formData.defaultShiftId) {
        saveData.defaultShiftId = formData.defaultShiftId;
      }

      await onSave(position?.id || null, saveData);

      // Success toast
      toast.success(position
        ? `Stanowisko "${formData.name}" zostało zaktualizowane`
        : `Stanowisko "${formData.name}" zostało dodane`
      );

      onClose();
    } catch (error: any) {
      const errorMessage = error.message || 'Wystąpił błąd podczas zapisywania stanowiska';
      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="sticky top-0 z-10 bg-background px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            {position ? `Edytuj stanowisko: ${position.name}` : 'Dodaj nowe stanowisko'}
          </DialogTitle>
          <DialogDescription>
            {position ? 'Zaktualizuj dane stanowiska' : 'Wypełnij formularz aby dodać stanowisko do systemu'}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4">
          <form id="position-form" onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="text-red-500 text-sm bg-red-50 p-3 rounded-md border border-red-200">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nazwa stanowiska <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="np. Operator linii produkcyjnej"
                  required
                />
              </div>
              <div>
                <Label htmlFor="departmentId">Dział</Label>
                <select
                  id="departmentId"
                  value={formData.departmentId}
                  onChange={(e) => setFormData({...formData, departmentId: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="">Brak działu</option>
                  {departments.map((dept: any) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="defaultShiftId">Domyślna zmianowość</Label>
              <select
                id="defaultShiftId"
                value={formData.defaultShiftId}
                onChange={(e) => setFormData({...formData, defaultShiftId: e.target.value})}
                disabled={!!shiftsError}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">Brak (zmianowość domyślna firmy)</option>
                {shiftsError && formData.defaultShiftId && (
                  <option value={formData.defaultShiftId}>{position?.defaultShiftName || 'Wybrana zmianowość'}</option>
                )}
                {shifts
                  .filter((s) => s.isActive || s.id === formData.defaultShiftId)
                  .map((shift) => (
                    <option key={shift.id} value={shift.id}>
                      {shift.name}{shift.isActive ? '' : ' (nieaktywna)'}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {shiftsError
                  ? 'Nie masz dostępu do listy zmianowości, więc nie możesz jej tu zmienić.'
                  : 'Używana dla osób na tym stanowisku, które nie mają przypisanej własnej zmianowości.'}
              </p>
            </div>

            <div>
              <Label htmlFor="description">Opis stanowiska</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Opisz obowiązki i wymagania dla tego stanowiska..."
                rows={3}
              />
            </div>

            {position && (
              <div>
                <Label htmlFor="isActive">Status</Label>
                <select
                  id="isActive"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={formData.isActive.toString()}
                  onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                >
                  <option value="true">Aktywne</option>
                  <option value="false">Nieaktywne</option>
                </select>
              </div>
            )}
          </form>
        </div>

        <DialogFooter className="sticky bottom-0 z-10 bg-background px-6 pb-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Anuluj
          </Button>
          <Button type="submit" form="position-form" disabled={saving}>
            {saving ? 'Zapisywanie...' : (position ? 'Zapisz zmiany' : 'Dodaj stanowisko')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
